import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "./DashboardLayout/DasboardLayout";
import { API_URL } from "./config";

const BASE = `${API_URL}/ride-intents`;

const RIDE_TYPES = [
  ["", "All ride types"],
  ["private", "Private"],
  ["rideshare", "Shared"],
  ["morning_dash", "Morning Dash"],
  ["delivery", "Delivery"],
];
const CHANNELS = [
  ["", "All channels"],
  ["web_app", "Web"],
  ["xgobot", "WhatsApp bot"],
  ["mobile_app", "Mobile app"],
];
const OUTCOMES = [
  ["", "Any outcome"],
  ["in_progress", "In progress"],
  ["abandoned", "Abandoned"],
  ["submitted", "Submitted"],
  ["waiting_for_match", "Waiting for a driver"],
  ["matched", "Matched"],
  ["completed", "Completed"],
  ["no_match", "No match"],
  ["cancelled", "Cancelled"],
];
const FOLLOW_UP_OUTCOMES = [
  ["reached_will_book", "Reached - will book"],
  ["reached_not_interested", "Reached - not interested"],
  ["no_answer", "No answer"],
  ["wrong_number", "Wrong number"],
  ["do_not_contact", "Do not contact"],
];
const OUTCOME_STYLE = {
  completed: "bg-green-100 text-green-800",
  matched: "bg-green-100 text-green-800",
  waiting_for_match: "bg-yellow-100 text-yellow-800",
  in_progress: "bg-blue-100 text-blue-800",
  abandoned: "bg-red-100 text-red-800",
  no_match: "bg-red-100 text-red-800",
  cancelled: "bg-gray-200 text-gray-700",
  submitted: "bg-blue-100 text-blue-800",
};
const label = (list, value) => (list.find(([v]) => v === value) || [value, value || "-"])[1];

// Harare calendar date (UTC+2, no daylight saving) as YYYY-MM-DD.
const harareToday = () => new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 10);
const harareTime = (iso) => (iso ? new Date(new Date(iso).getTime() + 2 * 3600 * 1000).toISOString().slice(11, 16) : "-");
const harareDay = (iso) => (iso ? new Date(new Date(iso).getTime() + 2 * 3600 * 1000).toISOString().slice(0, 10) : "");

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="rounded-lg border p-2 text-sm">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

function Funnel({ bucket }) {
  const steps = [
    ["Started", bucket.started], ["Fare shown", bucket.quoted], ["Submitted", bucket.submitted],
    ["Matched", bucket.matched], ["Completed", bucket.completed],
  ];
  const base = bucket.started || 1;
  return (
    <div>
      <div className="grid grid-cols-5 gap-2">
        {steps.map(([name, n]) => (
          <div key={name} className="rounded-lg bg-gray-50 p-3 text-center dark:bg-gray-700">
            <p className="text-xs text-gray-500">{name}</p>
            <p className="text-xl font-bold text-gray-800 dark:text-white">{n}</p>
            <p className="text-xs text-gray-500">{Math.round((n / base) * 100)}%</p>
          </div>
        ))}
      </div>
      <p className="mt-2 text-xs text-gray-500">
        Never matched: {bucket.no_match} · Abandoned: {bucket.abandoned}. Percentages are of everyone who started.
      </p>
    </div>
  );
}

export default function RideIntents() {
  const today = harareToday();
  const [filters, setFilters] = useState({ date: today, date_to: today, ride_type: "", channel: "", outcome: "", search: "", unmatched_only: false });
  const [data, setData] = useState(null);
  const [summary, setSummary] = useState(null);
  const [unnamed, setUnnamed] = useState([]);
  const [areaInputs, setAreaInputs] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [followUp, setFollowUp] = useState(null);

  const admin = () => localStorage.getItem("userName") || "dashboard";

  const query = useCallback(() => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== false) p.set(k, v); });
    return p.toString();
  }, [filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setMessage("");
    try {
      const q = query();
      const [list, sum, areas] = await Promise.all([
        fetch(`${BASE}?${q}&limit=300`).then((r) => r.json()),
        fetch(`${BASE}/summary?${q}`).then((r) => r.json()),
        fetch(`${BASE}/area-names/unnamed?limit=15`).then((r) => r.json()),
      ]);
      if (!list.success) throw new Error(list.message || "Failed to load intents");
      setData(list);
      setSummary(sum.success ? sum : null);
      setUnnamed(areas.success ? areas.data : []);
    } catch (e) {
      setMessage(`Failed to load: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [query]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (k, v) => setFilters((prev) => ({ ...prev, [k]: v }));

  const saveFollowUp = async () => {
    try {
      const res = await fetch(`${BASE}/${followUp.intent_id}/follow-up`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ changed_by: admin(), outcome: followUp.outcome, note: followUp.note }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Failed to save");
      setFollowUp(null);
      load();
    } catch (e) {
      setMessage(e.message);
    }
  };

  const saveArea = async (cell) => {
    const name = (areaInputs[`${cell.cell}:${cell.h3_resolution}`] || "").trim();
    if (!name) return;
    try {
      const res = await fetch(`${BASE}/area-names`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ h3_cell: cell.cell, h3_resolution: cell.h3_resolution, area_name: name, changed_by: admin() }),
      });
      const json = await res.json();
      if (!res.ok || !json.success) throw new Error(json.message || "Failed to save");
      load();
    } catch (e) {
      setMessage(e.message);
    }
  };

  const typeRows = summary ? Object.entries(summary.by_ride_type) : [];

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl p-6">
        <h2 className="mb-1 text-2xl font-bold text-gray-800 dark:text-white">Ride Intents</h2>
        <p className="mb-4 text-sm text-gray-500">
          Every ride attempt, from the moment a rider chooses a destination: how many started, saw a fare, booked, got matched and completed — and who
          did not, so staff can follow up. Outcomes come from the live status of the trip or request, so they cannot drift.
          Times are Harare time.
        </p>
        <p className="mb-6 rounded-lg bg-yellow-50 p-3 text-xs text-yellow-900">
          This page shows customer names and phone numbers. Use them only to help the rider with their ride, record each call below so nobody is called twice,
          and do not copy or share the list.
        </p>

        <div className="mb-6 flex flex-wrap items-end gap-3 rounded-lg bg-white p-4 shadow dark:bg-gray-800">
          <label className="text-xs text-gray-500">From<br /><input type="date" value={filters.date} max={today} onChange={(e) => set("date", e.target.value)} className="rounded-lg border p-2 text-sm" /></label>
          <label className="text-xs text-gray-500">To<br /><input type="date" value={filters.date_to} max={today} onChange={(e) => set("date_to", e.target.value)} className="rounded-lg border p-2 text-sm" /></label>
          <Select value={filters.ride_type} onChange={(v) => set("ride_type", v)} options={RIDE_TYPES} />
          <Select value={filters.channel} onChange={(v) => set("channel", v)} options={CHANNELS} />
          <Select value={filters.outcome} onChange={(v) => set("outcome", v)} options={OUTCOMES} />
          <input placeholder="Search name, phone or id" value={filters.search} onChange={(e) => set("search", e.target.value)} className="rounded-lg border p-2 text-sm" />
          <label className="flex items-center gap-1.5 text-sm">
            <input type="checkbox" checked={filters.unmatched_only} onChange={(e) => set("unmatched_only", e.target.checked)} /> Unmatched only
          </label>
          <button onClick={load} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">{loading ? "Loading..." : "Refresh"}</button>
        </div>

        {message && <p className="mb-4 text-sm text-red-600">{message}</p>}

        {summary && (
          <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
            <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white">Funnel</h3>
            <Funnel bucket={summary.total} />
            {typeRows.length > 0 && (
              <table className="mt-4 w-full text-left text-sm">
                <thead>
                  <tr className="border-b text-xs text-gray-500">
                    <th className="py-2">Ride type</th><th>Started</th><th>Fare shown</th><th>Submitted</th><th>Matched</th><th>Completed</th><th>No match</th><th>Abandoned</th>
                  </tr>
                </thead>
                <tbody>
                  {typeRows.map(([type, b]) => (
                    <tr key={type} className="border-b">
                      <td className="py-2">{type === "unchosen" ? "Not chosen yet" : label(RIDE_TYPES, type)}</td>
                      <td>{b.started}</td><td>{b.quoted}</td><td>{b.submitted}</td><td>{b.matched}</td><td>{b.completed}</td><td>{b.no_match}</td><td>{b.abandoned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        <div className="mb-6 overflow-x-auto rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-3 text-lg font-semibold text-gray-800 dark:text-white">
            Attempts {data ? `(${data.count}${data.count >= 300 ? "+" : ""})` : ""}
          </h3>
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b text-xs text-gray-500">
                <th className="py-2">When</th><th>Rider</th><th>Channel</th><th>Type</th><th>Trip</th><th>Fare</th><th>Outcome</th><th>Follow-up</th>
              </tr>
            </thead>
            <tbody>
              {data && data.intents.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-gray-500">No attempts for these filters.</td></tr>
              )}
              {data && data.intents.map((i) => (
                <tr key={i.intent_id} className="border-b align-top">
                  <td className="py-2 whitespace-nowrap">{harareDay(i.created_at)}<br /><span className="text-xs text-gray-500">{harareTime(i.created_at)}</span></td>
                  <td>
                    {i.passenger_name || `#${i.passenger_id}`}
                    {i.passenger_phone && <><br /><a href={`tel:${i.passenger_phone}`} className="text-xs text-blue-600 underline">{i.passenger_phone}</a></>}
                  </td>
                  <td>{label(CHANNELS, i.channel)}</td>
                  <td>{i.ride_type ? label(RIDE_TYPES, i.ride_type) : "-"}</td>
                  <td className="max-w-xs">
                    <span className="block truncate">{i.pickup_area || i.pickup_name || "-"}</span>
                    <span className="block truncate text-xs text-gray-500">to {i.dropoff_area || i.dropoff_name || "-"}</span>
                    {i.linked_id ? <span className="text-xs text-gray-400">{i.linked_type} #{i.linked_id}{i.linked_status ? ` (${i.linked_status})` : ""}</span> : null}
                  </td>
                  <td>{i.quoted_fare != null ? `$${Number(i.quoted_fare).toFixed(2)}` : "-"}</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-xs ${OUTCOME_STYLE[i.outcome] || "bg-gray-100"}`}>{label(OUTCOMES, i.outcome)}</span></td>
                  <td className="text-xs">
                    {i.follow_up_at ? (
                      <>
                        {label(FOLLOW_UP_OUTCOMES, i.follow_up_outcome)}<br />
                        <span className="text-gray-500">{i.follow_up_by} · {harareDay(i.follow_up_at)} {harareTime(i.follow_up_at)}</span>
                        {i.follow_up_note && <span className="block text-gray-500">{i.follow_up_note}</span>}
                      </>
                    ) : (
                      ["abandoned", "no_match", "waiting_for_match"].includes(i.outcome) && i.passenger_phone ? (
                        <button className="text-blue-600 underline" onClick={() => setFollowUp({ intent_id: i.intent_id, outcome: "reached_will_book", note: "" })}>Log follow-up</button>
                      ) : "-"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {followUp && (
            <div className="mt-4 rounded-lg border bg-gray-50 p-4 dark:bg-gray-700">
              <h4 className="mb-2 font-semibold text-gray-800 dark:text-white">Log follow-up for attempt #{followUp.intent_id}</h4>
              <div className="flex flex-wrap gap-3">
                <Select value={followUp.outcome} onChange={(v) => setFollowUp({ ...followUp, outcome: v })} options={FOLLOW_UP_OUTCOMES} />
                <input placeholder="Note (optional)" value={followUp.note} onChange={(e) => setFollowUp({ ...followUp, note: e.target.value })} className="min-w-[16rem] flex-1 rounded-lg border p-2 text-sm" />
                <button onClick={saveFollowUp} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">Save</button>
                <button onClick={() => setFollowUp(null)} className="rounded-lg border px-4 py-2 text-sm">Cancel</button>
              </div>
              <p className="mt-2 text-xs text-gray-500">Recorded as {admin()}.</p>
            </div>
          )}
        </div>

        <div className="mb-6 rounded-lg bg-white p-5 shadow dark:bg-gray-800">
          <h3 className="mb-1 text-lg font-semibold text-gray-800 dark:text-white">Name these areas</h3>
          <p className="mb-3 text-xs text-gray-500">
            The busiest map cells that have no name yet, so attempts show a suburb instead of “Unnamed area”. The sample is an address a rider typed or picked in that cell.
          </p>
          {unnamed.length === 0 ? (
            <p className="text-sm text-gray-500">Every busy area has a name.</p>
          ) : (
            <table className="w-full text-left text-sm">
              <thead><tr className="border-b text-xs text-gray-500"><th className="py-2">Sample address</th><th>Attempts</th><th>Area name</th><th /></tr></thead>
              <tbody>
                {unnamed.map((c) => {
                  const key = `${c.cell}:${c.h3_resolution}`;
                  return (
                    <tr key={key} className="border-b">
                      <td className="max-w-xs truncate py-2">{c.sample_name || c.cell}</td>
                      <td>{c.intents}</td>
                      <td><input value={areaInputs[key] || ""} onChange={(e) => setAreaInputs({ ...areaInputs, [key]: e.target.value })} placeholder="e.g. Avondale" className="w-full rounded border p-1.5 text-sm" /></td>
                      <td><button onClick={() => saveArea(c)} className="text-blue-600 underline">Save</button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
